import { SelectedSource, schSourceType } from '@aloft/toolkit-shared';
import { EntityState } from '@reduxjs/toolkit';

import { get, set } from 'lodash';

import { PropertyGridFieldName } from '../../../../components/PropertyGrid/types.ts';
import {
  CompPropertyDataState,
  PropertyDataSliceExtraReducer,
  PropertyDataState,
  SubjectPropertyDataState,
} from '../../../types.ts';
import { resolveFeatureGridFieldConflicts } from '../../actions/resolveFeatureGridFieldConflicts.ts';
import { propertyDataAdapter } from '../../propertyDataAdapter.ts';
import { getBumpedSyncState } from '../../utils/getBumpedSyncState.ts';

const getResolvedField = (
  state: EntityState<PropertyDataState, string>,
  fieldName: PropertyGridFieldName,
  propertyId: string,
): { id: string; changes: Partial<PropertyDataState> } => {
  const current = state.entities[propertyId];
  if (!current) return { id: propertyId, changes: {} };

  const isSubject = propertyId === 'subject';
  const propertyDataKey = isSubject ? 'subjectData' : 'compData';
  const propertyData = isSubject
    ? (current as SubjectPropertyDataState).subjectData
    : (current as CompPropertyDataState).compData;

  const selectedSourceField = get(propertyData.selectedSources, fieldName) as
    | SelectedSource
    | undefined;

  const updatedSelectedSources = { ...propertyData.selectedSources };

  set(updatedSelectedSources, fieldName, {
    source: selectedSourceField?.source ?? schSourceType.enum.none,
    justification: selectedSourceField?.justification,
    conflictIsResolved: true,
  });

  return {
    id: propertyId,
    changes: {
      id: propertyId,
      syncState: getBumpedSyncState(current.syncState),
      [propertyDataKey]: {
        ...propertyData,
        selectedSources: updatedSelectedSources,
      },
    },
  };
};

export const featureGridConflictedFieldsExtraReducers: PropertyDataSliceExtraReducer = (
  builder,
) => {
  builder.addCase(resolveFeatureGridFieldConflicts, (state, action) => {
    const fieldsToResolve = Object.values(action.payload).filter((v) => !!v);

    propertyDataAdapter.updateMany(
      state,
      fieldsToResolve.map(({ fieldName, propertyId }) =>
        getResolvedField(state, fieldName, propertyId),
      ),
    );
  });
};
